import { useDispatch, useSelector } from "react-redux";
import ProfileImage from "../../assets/images/Avatars.png"
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { EditProfile } from "../../services/manageUserServices";
import { fetchUsers } from "../../redux/slices/userSlice";
import { getRole } from "../../redux/slices/getRoleSlice";
import { toast } from "react-toastify";

const ProfileInformation = () => {
  const [isEditable, setIsEditable] = useState(false);
  const [imageFile, setImageFile] = useState('')

  const navigate = useNavigate();
  const dispatch = useDispatch()

  const user = useSelector((state) => state?.auth);
  const roles = useSelector((state) => state.roles?.roles?.data);

  useEffect(() =>{
    dispatch(getRole())
    dispatch(fetchUsers())
  }, [dispatch]);

  const userProfile = user?.user?.data?.profile;

  useEffect(() => {
    if (userProfile?.profileImage?.length > 0) {
      setImageFile(userProfile?.profileImage);
    }
  }, [userProfile]);

  const toggleEdit = () => {
    setIsEditable(!isEditable);
  };
  
  const formik = useFormik({
      initialValues: {
        userName: "",
        phoneNumber: "",
        email: "",
        roleName: "",
        profileImage: "",
      },

      validationSchema: Yup.object({
          userName: Yup.string().required('UserName is required'),
          phoneNumber: Yup.string().required('phoneNumber is required'),
          roleName: Yup.string().required('RoleName is required')
                .oneOf(["Admin", "SuperAdmin", "Staff"], 'Invalid option'),
          email: Yup.string().email('Invalid email address').required('Email is required'),
      }),
      onSubmit: async (values) => {
        try {
          const res = await EditProfile(values)
        if (res.statusCode === 200) {
          dispatch(fetchUsers())
          toast.success(res.message);
          setIsEditable(false)
          navigate('/settings'); 
        } else if (res.statusCode === 205) {
          toast.error(res.message);
        } else if(res?.statusCode === 404){
          toast.error(res.message)
        } else {
          toast.error(res.message)
        }
        } catch (error) {
          toast.error('An error occurred during user creation');
        } finally {
        }
    }
  });

  useEffect(() => {
    if(userProfile){
      formik.setFieldValue("userName", userProfile?.userName);
      formik.setFieldValue("email", userProfile?.email);
      formik.setFieldValue("roleName", userProfile?.roleName);
      formik.setFieldValue("phoneNumber", userProfile?.phoneNumber);

      if(userProfile?.profileImage){
        formik.setFieldValue("profileImage", userProfile?.profileImage);
      }
    }
  },[userProfile]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImageFile(base64String); 
        formik.setFieldValue("profileImage", base64String); 
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex items-center justify-center w-full h-full">
      <div className="bg-white p-7 border rounded-xl max-w-md">
        <h3 className="text-2xl text-center px-4 text-[#FF0000]">
          Profile Information
        </h3>
        <p className="text-center text-[#0A0A0A]/80 text-sm mt-4">
          Update your profile details to keep your account secure and ensure
          accurate information for smooth operations.
        </p>
        <div className="flex justify-center mt-8 relative">
        <div className="grid place-items-center mt-8">
            {imageFile ? (
              <label className="relative cursor-pointer">
                <img
                  src={imageFile}
                  alt="Selected"
                  className={`${!isEditable ? "cursor-default": "cursor-pointer"} w-[100px] h-[100px] rounded-full border border-2 border-orange object-cover mb-4`}
                />
                <input
                  type="file"
                  id="profileImage"
                  name="profileImage"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={!isEditable}
                />
              </label>
            ) : (
              <label className="cursor-pointer border rounded-lg">
                <img src={ProfileImage} alt="Placeholder" className="w-[100px] h-[100px] border border-2 border-orange rounded-full object-cover mb-4" />
                <input
                  type="file"
                  id="profileImage"
                  name="profileImage"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {formik.touched.profileImage && formik.errors.profileImage ? (
                  <div className="text-red-500 text-xs">{formik.errors.profileImage}</div>
                ) : null}
              </label>
            )}
          </div>
          <button 
            className={`${isEditable ? "text-primaryRed" : "text-black"} text-sm absolute right-0 bottom-0 hover:text-primaryRed`}
            type={isEditable ? "submit" : "button"}
            onClick={!isEditable ? toggleEdit : undefined}
          >  
            Edit Profile
          </button>
        </div>
        <form onSubmit={formik.handleSubmit} className="md:px-8 py-5">
          <label htmlFor="email" className="text-sm">User Name</label>
          <div className="mt-2 mb-5">
            <input
              id="userName"
              name="userName"
              disabled={!isEditable}
              value={formik.values.userName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="username"
              className="border border-[#00000] border-opacity-30 rounded-md px-4 w-full py-1.5 outline-none ring-0 text-sm"
            />
          </div>
          <div className="mb-5">
            <label htmlFor="roleName" className="text-sm">RoleName</label>
            <div className="my-2">
              <select
                id="roleName"
                name="roleName"
                disabled={!isEditable}
                value={formik.values.roleName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="border border-[#00000] border-opacity-30 rounded-md px-4 w-full py-1.5 outline-none ring-0 text-sm focus:outline-0"
              >
                {!isEditable ? (
                  <option value="SuperAdmin" className="text-sm">SuperAdmin</option>
                ) : (
                  roles && roles.length > 0 ? (
                    roles.map((role) => (
                      <option key={role.roleId} value={role.name} className="text-sm">
                        {role.name}
                      </option>
                    ))
                  ) : (
                    <option disabled className="text-sm">No roles available</option>
                  )
                )}
              </select>
              {formik.touched.roleName && formik.errors.roleName ? (
                <span className="text-sm mt-1 text-red-500">{formik.errors.roleName}</span>
              ) : null}
            </div>
          </div>
          <label htmlFor="email" className="text-sm">E-Mail</label>
          <div className="mt-2 mb-5">
            <input
              id="email"
              name="email"
              disabled={!isEditable}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="email"
              className="border border-[#00000] border-opacity-30 rounded-md px-4 w-full py-1.5 outline-none ring-0 text-sm"
            />
          </div>
          <label htmlFor="email" className="text-sm">Phone Number</label>
          <div className="mt-2 mb-5">
            <input
              id="phoneNumber"
              name="phoneNumber"
              disabled={!isEditable}
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="phone number"
              className="border border-[#00000] border-opacity-30 rounded-md px-4 w-full py-1.5 outline-none ring-0 text-sm"
            />
          </div>
          {isEditable ? (
            <button
             type="submit"
             className="bg-[#FF0000] hover:bg-primaryRed/50 text-white text-md w-full px-4 py-2 rounded-md outline-none ring-0 border border-primaryRed hover:text-white capitalize mt-6 focus:outline-none focus:border-primaryRed hover:border-PrimaryRed"
             >
             save
           </button>
          ) : (
            <></>
          )
          }
        </form>
      </div>
    </div>
  );
};
export default ProfileInformation;
